import { useEffect, useState } from "react";
import biddingZonesData from "../../data/biddingZones.json";

/**
 * Returns the full bidding zone name for a given zone ID.
 * If zoneId is "all", it translates it using gettext("All") if provided, or returns "All".
 *
 * @param zoneId - Bidding zone ID (e.g. "SE3", "DK1", "NO1", "all")
 * @param countryCode - Optional ISO country code (e.g. "SE", "DK")
 * @param gettext - Optional gettext translation function for "All"
 * @returns Full zone name, translated "All", or original zone ID if not found
 */
export const getBiddingZoneName = (
  zoneId?: string | null,
  countryCode?: string | null,
  gettext?: (text: string) => string,
): string => {
  if (!zoneId || typeof zoneId !== "string") {
    return "";
  }

  const trimmed = zoneId.trim();
  if (!trimmed) {
    return "";
  }

  if (trimmed.toLowerCase() === "all") {
    return gettext ? gettext("All") : "All";
  }

  if (countryCode && typeof countryCode === "string") {
    const country = biddingZonesData.find(
      (item) =>
        item.iso_code?.toUpperCase() === countryCode.trim().toUpperCase() ||
        item.name?.toLowerCase() === countryCode.trim().toLowerCase(),
    );
    const match = country?.bidding_zones?.find(
      (z: any) => z.id?.toUpperCase() === trimmed.toUpperCase(),
    );
    if (match?.name) {
      return match.name;
    }
  }

  for (const country of biddingZonesData) {
    const match = country.bidding_zones?.find(
      (z: any) => z.id?.toUpperCase() === trimmed.toUpperCase(),
    );
    if (match?.name) {
      return match.name;
    }
  }

  return zoneId;
};

export const useBiddingZones = (countryCode: string | undefined) => {
  const [biddingZones, setBiddingZones] = useState<any>([]);

  useEffect(() => {
    if (countryCode) {
      const tmp = biddingZonesData.find(
        (item) => item.iso_code === countryCode,
      );
      if (tmp && tmp?.bidding_zones && tmp?.bidding_zones?.length) {
        const zones = tmp.bidding_zones.map((zone: any) => ({
          label: zone.name,
          value: zone.id,
          eic: zone.eic,
        }));
        setBiddingZones(zones);
      } else {
        setBiddingZones([]);
      }
    } else {
      setBiddingZones([]);
    }
  }, [countryCode]);

  return biddingZones;
};
